// Fonte: UManutContas.pas (14.162 linhas) + UManutContas.dfm (14.783 linhas)
// Tela de detalhe/criação de conta — Formik + Tabs
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Container,
  Card,
  Button,
  Spinner,
  Nav,
  Form as RBForm,
} from 'react-bootstrap';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FaArrowLeft } from 'react-icons/fa';
import Select from 'react-select';
import Header from '~/Components/Header/Header';

import {
  buscarConta,
  inserirConta,
  atualizarConta,
  sugerirCodigoConta,
  verificarContaExclusiva,
  verificarDuplicidadeCpf,
  listarRamosAtividade,
  listarGrupoContas,
  listarUsuariosResponsaveis,
  listarVendedoresContas,
  listarCobradoresContas,
  listarTodosGruposEmpresa,
  listarTabelasPrecosConta,
  listarCargosContas,
  listarBancosLista,
  listarTiposInternosContas,
  getCities,
  listarPerfisComprador,
  listarTabelasComissao,
  listarContasAtivas,
  listarCentrosCustoLookup,
  validarUsuarioSenha,
  getParametroChecaCgcCpf,
  listarRazoesSociaisLookup,
} from '~/Services/Service';
import AuthStorage from '~/Utils/AuthStorage';
import { caracteristicas } from '~/constant';
import { cleanCpfCnpj, format, validarCPF, validarCNPJ } from '~/Utils/Helpers';

import FormDadosGerais from './components/FormDadosGerais';
import FormTipo1 from './components/FormTipo1';
import FormTipo2 from './components/FormTipo2';
import FormTipo3 from './components/FormTipo3';
import FormTipo4 from './components/FormTipo4';
import FormTipo5 from './components/FormTipo5';
import FormTipo6 from './components/FormTipo6';
import FormTipo7 from './components/FormTipo7';
import FormTipo8 from './components/FormTipo8';
import ModalPropagarFornecedor from './components/ModalPropagarFornecedor';
import ModalBancoEmpreendimento from './components/ModalBancoEmpreendimento';
import ModalCadastroUnico from './components/ModalCadastroUnico';

import TabOcorrencias from './tabs/TabOcorrencias';
import TabConjugeSocio from './tabs/TabConjugeSocio';
import TabObservacoes from './tabs/TabObservacoes';
import TabColigados from './tabs/TabColigados';
import TabContasBancarias from './tabs/TabContasBancarias';
import TabEnderecos from './tabs/TabEnderecos';
import TabEnderecoCobranca from './tabs/TabEnderecoCobranca';
import TabEnderecoComercial from './tabs/TabEnderecoComercial';
import TabCentroCusto from './tabs/TabCentroCusto';
import TabJurosAuto from './tabs/TabJurosAuto';
import TabTarifas from './tabs/TabTarifas';
import TabInfoComerciais from './tabs/TabInfoComerciais';
import TabIndicesRemuneracao from './tabs/TabIndicesRemuneracao';
import TabSaldos from './tabs/TabSaldos';
import TabNegativarCpf from './tabs/TabNegativarCpf';

import './contas.css';

// Fonte: UManutContas.pas, sugereconta + BitBtn11Click
// Schema de validação base (todos os tipos)
var schemaBase = Yup.object({
  codigo_grupo_empresa: Yup.number()
    .required('Selecione o Grupo de Empresa')
    .typeError('Selecione o GE'),
  tipo: Yup.number()
    .required('Selecione o tipo de conta')
    .typeError('Tipo inválido'),
  codigo_conta: Yup.number()
    .required('Informe o código')
    .positive('Código deve ser positivo')
    .typeError('Código deve ser numérico'),
  apelido_conta: Yup.string()
    .required('Informe o apelido')
    .trim(),
  data_validade: Yup.string().required('Informe a data de validade'),
  codigo_ramo: Yup.number()
    .required('Selecione o ramo')
    .typeError('Ramo inválido'),
  status: Yup.string().required('Selecione o status'),
  permissao_neg_mata: Yup.string().required('Selecione o aceita'),
  aprovacao_1: Yup.number()
    .required('Selecione o aprovador')
    .typeError('Aprovador inválido'),
  aprovacao_2: Yup.number()
    .nullable()
    .transform(function(v, orig) {
      return orig === '' || orig == null ? null : v;
    })
    .typeError('Responsável inválido'),
  grupo: Yup.number().when('tipo', {
    is: function(tipo) {
      return tipo === 3 || tipo === 7;
    },
    then: Yup.number()
      .required('Categoria obrigatória para Despesa/Receita')
      .typeError('Categoria inválida'),
    otherwise: Yup.number().nullable(),
  }),
});

// Fonte: UManutContas.pas linhas 3299/3334/3381/3410 — Global.ParamCGCCPF = 'S' liga checagem.
// Default: ativo (conservador). Atualizado via GET /parametros/checa-cgccpf no load.
var paramCgcCpfAtivo = true;
var setParamCgcCpfAtivo = function(v) {
  paramCgcCpfAtivo = !!v;
};

// Fonte: UManutContas.pas, checaCGCCPF / Globais.pas ValidarCPF/ValidarCNPJ
// Validação de dígito verificador — CPF para PF, CNPJ para PJ
var validarDocumento = function(valor, tipoPessoa) {
  if (!paramCgcCpfAtivo) return true; // parâmetro global desliga a checagem
  if (!valor) return true; // campo vazio é tratado pelo .required()
  var limpo = cleanCpfCnpj(valor);
  if (limpo === '') return true;
  if (tipoPessoa === '0') return validarCPF(limpo);
  if (tipoPessoa === '1') return validarCNPJ(limpo);
  return limpo.length === 11 ? validarCPF(limpo) : validarCNPJ(limpo);
};

// Fonte: UManutContas.pas, BitBtn11Click — validações obrigatórias por tipo de conta
var SCHEMAS_POR_TIPO = {
  // Tipo 1 — Cliente Factoring (Ds_Cliente_Factoring)
  1: Yup.object({
    tipo1: Yup.object({
      razao_social: Yup.string().required('Informe o Nome / Razão Social'),
      endereco: Yup.string().required('Informe o Endereço'),
      codigo_cidade: Yup.mixed().required('Selecione a Cidade'),
      vendedor: Yup.mixed().required('Selecione o Vendedor'),
      cpf: Yup.string()
        .required('Informe o CPF/CNPJ')
        .test('cpf-cnpj', 'CPF/CNPJ inválido (dígito verificador)', function(
          val
        ) {
          return validarDocumento(val, this.parent.tipo_pessoa);
        }),
      tipo_pessoa: Yup.string().required('Selecione o Tipo de Pessoa'),
    }),
  }),
  // Tipo 2 — Cliente Comércio (Ds_ClienteComercio)
  2: Yup.object({
    tipo2: Yup.object({
      razao_social: Yup.string().required('Informe o Nome / Razão Social'),
      endereco: Yup.string().required('Informe o Endereço'),
      codigo_cidade: Yup.mixed().required('Selecione a Cidade'),
      cpf: Yup.string()
        .required('Informe o CPF/CNPJ')
        .test('cpf-cnpj', 'CPF/CNPJ inválido (dígito verificador)', function(
          val
        ) {
          return validarDocumento(val, this.parent.tipo_pessoa);
        }),
      tipo_pessoa: Yup.string().required('Selecione o Tipo de Pessoa'),
      vendedor: Yup.mixed().required('Selecione o Vendedor'),
      sexo: Yup.string().when('tipo_pessoa', {
        is: '0',
        then: Yup.string().required(
          'Selecione o Sexo (obrigatório para Pessoa Física)'
        ),
        otherwise: Yup.string().nullable(),
      }),
      estado_civil: Yup.string().when('tipo_pessoa', {
        is: '0',
        then: Yup.string().required(
          'Selecione o Estado Civil (obrigatório para Pessoa Física)'
        ),
        otherwise: Yup.string().nullable(),
      }),
    }),
  }),
  // Tipo 3 — Despesa: sem validações adicionais
  // Tipo 4 — Fornecedor (Ds_Fornecedor)
  4: Yup.object({
    tipo4: Yup.object({
      razao_social: Yup.string().required('Informe o Nome / Razão Social'),
      codigo_cidade: Yup.mixed().required('Selecione a Cidade'),
      cpf: Yup.string().when('internacional', {
        is: 'S',
        then: Yup.string().nullable(),
        otherwise: Yup.string()
          .required('Informe o CPF/CNPJ')
          .test('cpf-cnpj', 'CPF/CNPJ inválido (dígito verificador)', function(
            val
          ) {
            return validarDocumento(val, this.parent.tipo_pessoa);
          }),
      }),
      tipo_pessoa: Yup.string().required('Selecione o Tipo de Pessoa'),
      codigo_tabela: Yup.mixed().required('Selecione a Tabela de Preço'),
    }),
  }),
  // Tipo 5 — Banco (Ds_Banco)
  // Validação PIX (tipo + chave) feita imperativamente em handleGravar (~linha 528)
  // pois Yup.object().shape() com noSortEdges não funciona bem aninhado
  5: Yup.object({
    tipo5: Yup.object().shape({
      razao_social: Yup.string().required('Informe o Nome / Razão Social'),
      titular: Yup.string().required('Informe o Titular'),
      classe: Yup.string().required('Selecione a Classe'),
      liquidez: Yup.string().required('Selecione a Liquidez'),
      pix_chave: Yup.string().nullable(),
      pix_tipo: Yup.string().nullable(),
    }),
  }),
  // Tipo 6 — Interna (Ds_Interna)
  6: Yup.object({
    tipo6: Yup.object({
      tipo_interna: Yup.mixed().required('Selecione o Tipo Interno'),
      conta_cred_projeto: Yup.mixed().when('tipo_interna', {
        is: function(v) {
          return v === 2 || v === '2';
        },
        then: Yup.mixed().required(
          'Selecione a Conta Crédito Projeto (obrigatória para tipo Projeto)'
        ),
        otherwise: Yup.mixed().nullable(),
      }),
    }),
  }),
  // Tipo 7 — Receita: sem validações adicionais
  // Tipo 8 — Prestador de Serviço (Ds_Funcionario)
  8: Yup.object({
    tipo8: Yup.object({
      nome_prestador: Yup.string().required('Informe o Nome do Prestador'),
      codigo_cidade: Yup.mixed().required('Selecione a Cidade'),
      cgc: Yup.string()
        .required('Informe o CPF/CNPJ')
        .test('cpf-cnpj', 'CPF/CNPJ inválido (dígito verificador)', function(
          val
        ) {
          return validarDocumento(val, this.parent.tipo_pessoa);
        }),
      tipo: Yup.string().required('Selecione o Tipo de Prestador'),
      tipo_pessoa: Yup.string().required('Selecione o Tipo de Pessoa'),
    }),
  }),
};

// Retorna schema completo: base + tipo-específico
var getValidationSchema = function(tipo) {
  var tipoSchema = SCHEMAS_POR_TIPO[tipo];
  if (!tipoSchema) return schemaBase;
  return schemaBase.concat(tipoSchema);
};

var VALORES_INICIAIS = {
  codigo_grupo_empresa: '',
  tipo: '',
  grupo: null,
  codigo_conta: '',
  apelido_conta: '',
  codigo_contabil: '',
  data_validade: '',
  data_cadastro: '',
  codigo_tabela: null,
  codigo_ramo: '',
  aprovacao_1: null,
  aprovacao_2: '',
  status: '0',
  permissao_neg_mata: 'N',
  tipo1: {},
  tipo2: {},
  tipo3: {},
  tipo4: {},
  tipo5: {},
  tipo6: {},
  tipo7: {},
  tipo8: {},
};

var TABS_POR_TIPO = {
  // Fonte: UManutContas.pas ~linhas 2543-3179 — botões habilitados por tipo
  1: [
    'ocorrencias',
    'observacoes',
    'coligados',
    'contas-bancarias',
    'endereco-cobranca',
    'juros-auto',
    'tarifas',
    'indices-remuneracao',
    'info-comerciais',
    'saldos',
    'negativar-cpf',
  ],
  2: [
    'ocorrencias',
    'conjuges',
    'observacoes',
    'coligados',
    'contas-bancarias',
    'enderecos',
    'endereco-cobranca',
    'endereco-comercial',
    'juros-auto',
    'tarifas',
    'indices-remuneracao',
    'info-comerciais',
    'saldos',
    'negativar-cpf',
  ],
  3: [
    'ocorrencias',
    'observacoes',
    'contas-bancarias',
    'centro-custo',
    'indices-remuneracao',
    'saldos',
  ],
  4: [
    'ocorrencias',
    'observacoes',
    'coligados',
    'contas-bancarias',
    'juros-auto',
    'tarifas',
    'indices-remuneracao',
    'info-comerciais',
    'saldos',
    'negativar-cpf',
  ],
  5: [
    'ocorrencias',
    'observacoes',
    'contas-bancarias',
    'tarifas',
    'indices-remuneracao',
    'saldos',
  ],
  6: [
    'ocorrencias',
    'observacoes',
    'contas-bancarias',
    'centro-custo',
    'juros-auto',
    'indices-remuneracao',
    'saldos',
  ],
  7: [
    'ocorrencias',
    'observacoes',
    'contas-bancarias',
    'centro-custo',
    'indices-remuneracao',
    'saldos',
  ],
  8: [
    'ocorrencias',
    'observacoes',
    'coligados',
    'contas-bancarias',
    'juros-auto',
    'tarifas',
    'indices-remuneracao',
    'info-comerciais',
    'saldos',
    'negativar-cpf',
  ],
};

var TAB_TITLES = {
  ocorrencias: 'Ocorrências',
  conjuges: 'Cônjuge/Sócio',
  observacoes: 'Observações',
  coligados: 'Coligados',
  'contas-bancarias': 'Contas Bancárias',
  enderecos: 'Endereços',
  'endereco-cobranca': 'End. Cobrança',
  'endereco-comercial': 'End. Comercial',
  'centro-custo': 'Centro Custo',
  'juros-auto': 'Juros Auto',
  tarifas: 'Tarifas',
  'info-comerciais': 'Inf. Comerciais',
  'indices-remuneracao': 'Índices Rem.',
  saldos: 'Saldos',
  'negativar-cpf': 'Negativar CPF',
};

// ──────────────────────────────────────────────────────────────────────
// ContaTabs — componente isolado com seu próprio state
// Clicar em abas NÃO re-renderiza o Formik nem o formulário principal
// ──────────────────────────────────────────────────────────────────────
var ContaTabs = function(_ref) {
  var ge = _ref.ge;
  var codigo = _ref.codigo;
  var tabsAtivas = _ref.tabsAtivas;
  var conta = _ref.conta;

  var [tabAtiva, setTabAtiva] = useState(
    tabsAtivas.length > 0 ? tabsAtivas[0] : 'ocorrencias'
  );

  // Se o tipo mudar e a tab ativa não existir mais, reseta para a primeira
  useEffect(
    function() {
      if (tabsAtivas.length > 0 && tabsAtivas.indexOf(tabAtiva) === -1) {
        setTabAtiva(tabsAtivas[0]);
      }
    },
    [tabsAtivas, tabAtiva]
  );

  // Renderiza APENAS o conteúdo da tab ativa (não monta as outras)
  var renderTabContent = function() {
    switch (tabAtiva) {
      case 'ocorrencias':
        return <TabOcorrencias ge={ge} codigo={codigo} />;
      case 'conjuges':
        return <TabConjugeSocio ge={ge} codigo={codigo} />;
      case 'observacoes':
        return <TabObservacoes ge={ge} codigo={codigo} />;
      case 'coligados':
        return <TabColigados ge={ge} codigo={codigo} />;
      case 'contas-bancarias':
        return <TabContasBancarias ge={ge} codigo={codigo} />;
      case 'enderecos':
        return <TabEnderecos ge={ge} codigo={codigo} />;
      case 'endereco-cobranca':
        return <TabEnderecoCobranca ge={ge} codigo={codigo} />;
      case 'endereco-comercial':
        return <TabEnderecoComercial ge={ge} codigo={codigo} />;
      case 'centro-custo':
        return <TabCentroCusto ge={ge} codigo={codigo} />;
      case 'juros-auto':
        return <TabJurosAuto ge={ge} codigo={codigo} />;
      case 'tarifas':
        return <TabTarifas ge={ge} codigo={codigo} />;
      case 'info-comerciais':
        return <TabInfoComerciais ge={ge} codigo={codigo} />;
      case 'indices-remuneracao':
        return <TabIndicesRemuneracao ge={ge} codigo={codigo} />;
      case 'saldos':
        return <TabSaldos ge={ge} codigo={codigo} />;
      case 'negativar-cpf':
        return (
          <TabNegativarCpf
            ge={ge}
            codigo={codigo}
            cpf={conta && conta.dados_tipo ? conta.dados_tipo.cpf : null}
          />
        );
      default:
        return null;
    }
  };

  if (tabsAtivas.length === 0) return null;

  return (
    <Card className="shadow-sm mt-3">
      <Card.Body className="p-2">
        <Nav
          variant="tabs"
          activeKey={tabAtiva}
          onSelect={setTabAtiva}
          className="mb-2 contas-tabs-nav flex-nowrap"
        >
          {tabsAtivas.map(function(tabKey) {
            return (
              <Nav.Item key={tabKey}>
                <Nav.Link eventKey={tabKey}>
                  {TAB_TITLES[tabKey] || tabKey}
                </Nav.Link>
              </Nav.Item>
            );
          })}
        </Nav>
        <div className="pt-2">{renderTabContent()}</div>
      </Card.Body>
    </Card>
  );
};

// ──────────────────────────────────────────────────────────────────────
// ContaDetalhe — componente principal
// ──────────────────────────────────────────────────────────────────────
var ContaDetalhe = function(_ref) {
  var match = _ref.match;
  var history = useHistory();

  var geParam = match && match.params ? parseInt(match.params.ge, 10) : null;
  var codigoParam =
    match && match.params ? parseInt(match.params.codigo, 10) : null;
  var isNovo = !geParam || !codigoParam;

  var [conta, setConta] = useState(null);
  var [loading, setLoading] = useState(!isNovo);
  var [modoEdicao, setModoEdicao] = useState(false);
  var [lookups, setLookups] = useState({
    ramos: [],
    responsaveis: [],
    vendedoresRaw: [],
    cobradoresRaw: [],
    gruposContas: [],
    ges: [],
    tabelasPrecos: [],
    cargos: [],
    cidades: [],
    bancosLista: [],
    tiposInternos: [],
    perfis: [],
    tabelasComissao: [],
    contasAtivas: [],
    centrosCusto: [],
    razoesSociais: [],
  });
  var [lookupsLoading, setLookupsLoading] = useState(true);
  var [dadosOriginais, setDadosOriginais] = useState(null);
  var [showModalFornecedor, setShowModalFornecedor] = useState(false);
  var [showModalBanco, setShowModalBanco] = useState(false);
  var [showModalCadUnico, setShowModalCadUnico] = useState(false);
  var [contaSalva, setContaSalva] = useState({
    ge: geParam,
    codigo: codigoParam,
  });

  // Tipo selecionado — rastreia o valor atual para computar o schema de validação
  var [tipoSelecionado, setTipoSelecionado] = useState(
    isNovo ? null : conta ? conta.tipo : null
  );
  var [codigoLoading, setCodigoLoading] = useState(false);
  var [senhaAprovador, setSenhaAprovador] = useState('');

  // Ref para acessar formik fora do render prop
  var formikRef = useRef(null);

  // Carrega lookups — batch único via Promise.all (1 setLookups em vez de 5)
  useEffect(function() {
    var mapLookup = function(item) {
      return {
        value: item.codigo,
        label:
          item.descricao !== null && item.descricao !== undefined
            ? item.descricao
            : String(item.codigo),
      };
    };

    // Fonte: UManutContas.pas — Global.ParamCGCCPF: flag condiciona validação dígito CPF/CNPJ.
    getParametroChecaCgcCpf()
      .then(function(res) {
        var ativo =
          res && res.data && typeof res.data.ativo === 'boolean'
            ? res.data.ativo
            : true;
        setParamCgcCpfAtivo(ativo);
      })
      .catch(function() {
        setParamCgcCpfAtivo(true);
      });

    Promise.all([
      listarRamosAtividade().catch(function() {
        return [];
      }),
      listarUsuariosResponsaveis().catch(function() {
        return [];
      }),
      listarVendedoresContas().catch(function() {
        return [];
      }),
      listarTodosGruposEmpresa().catch(function() {
        return [];
      }),
      listarTabelasPrecosConta().catch(function() {
        return [];
      }),
      listarCobradoresContas().catch(function() {
        return [];
      }),
      listarCargosContas().catch(function() {
        return [];
      }),
      getCities().catch(function() {
        return [];
      }),
      listarBancosLista().catch(function() {
        return [];
      }),
      listarTiposInternosContas().catch(function() {
        return [];
      }),
      listarPerfisComprador().catch(function() {
        return [];
      }),
      listarTabelasComissao().catch(function() {
        return [];
      }),
      listarContasAtivas().catch(function() {
        return [];
      }),
      listarRazoesSociaisLookup().catch(function() {
        return { data: [] };
      }),
    ]).then(function(results) {
      var ramosData = Array.isArray(results[0]) ? results[0] : [];
      var responsaveisData = Array.isArray(results[1]) ? results[1] : [];
      var vendedoresData = Array.isArray(results[2]) ? results[2] : [];
      var gesData = Array.isArray(results[3]) ? results[3] : [];
      var tabelasData = Array.isArray(results[4]) ? results[4] : [];
      var cobradoresData = Array.isArray(results[5]) ? results[5] : [];
      var cargosData = Array.isArray(results[6]) ? results[6] : [];
      var cidadesData = Array.isArray(results[7]) ? results[7] : [];
      var bancosData = Array.isArray(results[8]) ? results[8] : [];
      var tiposInternosData = Array.isArray(results[9]) ? results[9] : [];
      var perfisData = Array.isArray(results[10]) ? results[10] : [];
      var tabelasComissaoData = Array.isArray(results[11]) ? results[11] : [];
      var contasAtivasData = Array.isArray(results[12]) ? results[12] : [];
      // Fonte: UManutContas.pas:4677-4682 — cdrazao "SELECT razao, codigo FROM razao_social"
      var razoesResp = results[13];
      var razoesData = Array.isArray(razoesResp)
        ? razoesResp
        : razoesResp && Array.isArray(razoesResp.data)
        ? razoesResp.data
        : [];

      var vendedoresRaw = vendedoresData.map(function(item) {
        return {
          value: item.codigo,
          label:
            item.descricao !== null && item.descricao !== undefined
              ? item.descricao
              : String(item.codigo),
          ge: item.codigo_grupo_empresa,
        };
      });

      setLookups(function(l) {
        return {
          ...l,
          ramos: ramosData.map(mapLookup),
          responsaveis: responsaveisData.map(mapLookup),
          vendedoresRaw: vendedoresRaw,
          cobradoresRaw: cobradoresData.map(function(item) {
            return {
              value: item.codigo,
              label:
                item.descricao !== null && item.descricao !== undefined
                  ? item.descricao
                  : String(item.codigo),
              ge: item.codigo_grupo_empresa,
            };
          }),
          cargos: cargosData.map(mapLookup),
          cidades: cidadesData.map(function(c) {
            var cod = c.codigo || c.Codigo;
            var nome = c.cidade || c.Cidade || c.descricao || '';
            var uf = c.estado || c.Estado || '';
            // Fonte: UManutContas.pas:6443 — validação faixa CEP da cidade (Tipo 8)
            var cepIni =
              c.cep_inicial !== undefined ? c.cep_inicial : c.Cep_Inicial;
            var cepFim = c.cep_final !== undefined ? c.cep_final : c.Cep_Final;
            // Fonte: UManutContas.dfm — DDD/UF/País renderizados readonly via Ds_Tab_Cidades
            // DDD é numeric(19,6) no schema — truncar para inteiro
            var dddRaw = c.ddd !== undefined ? c.ddd : c.DDD;
            var ddd =
              dddRaw !== null && dddRaw !== undefined && dddRaw !== ''
                ? Math.trunc(Number(dddRaw))
                : null;
            var pais = c.pais !== undefined ? c.pais : c.Pais;
            return {
              value: cod,
              label: nome + (uf ? ' - ' + uf : ''),
              cepInicial: cepIni,
              cepFinal: cepFim,
              ddd: ddd,
              uf: uf,
              pais: pais,
            };
          }),
          bancosLista: bancosData.map(function(b) {
            var cod = b.codigo || b.Codigo || '';
            var desc = b.descricao || b.Descricao || '';
            return { value: cod, label: cod + (desc ? ' - ' + desc : '') };
          }),
          tiposInternos: tiposInternosData.map(mapLookup),
          perfis: perfisData.map(mapLookup),
          tabelasComissao: tabelasComissaoData.map(mapLookup),
          contasAtivas: contasAtivasData.map(mapLookup),
          ges: gesData.map(function(ge) {
            return {
              value: ge.codigo,
              label: ge.codigo + ' - ' + (ge.apelido || ge.codigo),
            };
          }),
          tabelasPrecos: tabelasData.map(mapLookup),
          razoesSociais: razoesData.map(function(item) {
            var cod = item.codigo !== undefined ? item.codigo : item.Codigo;
            var desc =
              item.descricao !== undefined
                ? item.descricao
                : item.Descricao !== undefined
                ? item.Descricao
                : item.razao;
            return { value: cod, label: (desc ? desc : '') + ' - ' + cod };
          }),
        };
      });
      setLookupsLoading(false);
    });
  }, []);

  // Carrega dados da conta (edição)
  useEffect(
    function() {
      if (isNovo) return;
      setLoading(true);
      buscarConta(geParam, codigoParam)
        .then(function(resp) {
          var data = resp;
          setConta(data);
          if (data.tipo) setTipoSelecionado(data.tipo);
          // Carrega grupos de contas para o tipo da conta
          if (data.tipo) {
            listarGrupoContas(data.tipo)
              .then(function(gResp) {
                var gData = Array.isArray(gResp) ? gResp : [];
                setLookups(function(l) {
                  return {
                    ...l,
                    gruposContas: gData.map(function(g) {
                      return {
                        value: g.codigo,
                        label:
                          g.descricao !== null && g.descricao !== undefined
                            ? g.descricao
                            : g.codigo,
                      };
                    }),
                  };
                });
              })
              .catch(function() {});
          }
          // Carrega centros de custo para o GE (necessário para FormTipo3)
          if (geParam) {
            listarCentrosCustoLookup(geParam)
              .then(function(ccResp) {
                var ccData = Array.isArray(ccResp) ? ccResp : [];
                setLookups(function(l) {
                  return Object.assign({}, l, {
                    centrosCusto: ccData.map(function(item) {
                      return {
                        value: item.codigo,
                        label:
                          item.descricao !== null &&
                          item.descricao !== undefined
                            ? item.descricao
                            : String(item.codigo),
                      };
                    }),
                  });
                });
              })
              .catch(function() {});
          }
        })
        .catch(function() {
          toast.error('Erro ao carregar conta');
          history.push('/contas');
        })
        .finally(function() {
          setLoading(false);
        });
    },
    [isNovo, geParam, codigoParam, history]
  );

  var verificarPermissaoEdicao = function(tipo) {
    if (tipo === 1 || tipo === 2) {
      if (!AuthStorage.temCaracteristica(caracteristicas.AlterarContaCliente)) {
        toast.error('Sem permissão para alterar conta de cliente (perm. 49)');
        return false;
      }
    }
    if (tipo === 2) {
      if (!AuthStorage.temCaracteristica(caracteristicas.AlterarDadosCliente)) {
        toast.error('Sem permissão para alterar dados do cliente (perm. 50)');
        return false;
      }
    }
    if (tipo === 3 || tipo === 7) {
      if (!AuthStorage.temCaracteristica(caracteristicas.ManutencaoDespesa)) {
        toast.error(
          'Sem permissão para alterar contas de despesa/receita (perm. 383)'
        );
        return false;
      }
    }
    return true;
  };

  var handleEditar = function() {
    if (conta && !verificarPermissaoEdicao(conta.tipo)) return;
    // Verificar conta exclusiva
    verificarContaExclusiva(geParam, codigoParam)
      .then(function(resp) {
        if (resp && resp.is_exclusiva) {
          toast.error('Esta conta é exclusiva e não pode ser editada');
          return;
        }
        // Snapshot para detecção de adulteração (tipo 2)
        if (conta && conta.tipo === 2 && conta.dados_tipo) {
          setDadosOriginais({
            endereco: conta.dados_tipo.endereco || null,
            razao_social: conta.dados_tipo.razao_social || null,
            cpf: conta.dados_tipo.cpf || null,
          });
        }
        setModoEdicao(true);
      })
      .catch(function() {
        // Se o endpoint não existir ainda, permite edição
        setModoEdicao(true);
      });
  };

  var getValoresIniciais = function() {
    if (!conta) return VALORES_INICIAIS;
    return {
      codigo_grupo_empresa:
        conta.codigo_grupo_empresa !== null &&
        conta.codigo_grupo_empresa !== undefined
          ? conta.codigo_grupo_empresa
          : '',
      tipo: conta.tipo !== null && conta.tipo !== undefined ? conta.tipo : '',
      grupo:
        conta.grupo !== null && conta.grupo !== undefined ? conta.grupo : null,
      codigo_conta:
        conta.codigo_conta !== null && conta.codigo_conta !== undefined
          ? conta.codigo_conta
          : '',
      apelido_conta:
        conta.apelido_conta !== null && conta.apelido_conta !== undefined
          ? conta.apelido_conta
          : '',
      codigo_contabil:
        conta.codigo_contabil !== null && conta.codigo_contabil !== undefined
          ? conta.codigo_contabil
          : '',
      data_validade: conta.data_validade
        ? moment(conta.data_validade).format('YYYY-MM-DD')
        : '',
      data_cadastro: conta.data_cadastro
        ? format.datetime(conta.data_cadastro)
        : '',
      codigo_tabela:
        conta.codigo_tabela !== null && conta.codigo_tabela !== undefined
          ? conta.codigo_tabela
          : null,
      codigo_ramo:
        conta.codigo_ramo !== null && conta.codigo_ramo !== undefined
          ? conta.codigo_ramo
          : '',
      aprovacao_1:
        conta.aprovacao_1 !== null && conta.aprovacao_1 !== undefined
          ? conta.aprovacao_1
          : null,
      aprovacao_2:
        conta.aprovacao_2 !== null && conta.aprovacao_2 !== undefined
          ? conta.aprovacao_2
          : '',
      status:
        conta.status !== null && conta.status !== undefined
          ? String(conta.status)
          : '0',
      permissao_neg_mata:
        conta.permissao_neg_mata !== null &&
        conta.permissao_neg_mata !== undefined
          ? conta.permissao_neg_mata
          : 'N',
      tipo1: conta.tipo === 1 && conta.dados_tipo ? conta.dados_tipo : {},
      tipo2: conta.tipo === 2 && conta.dados_tipo ? conta.dados_tipo : {},
      tipo3: conta.tipo === 3 && conta.dados_tipo ? conta.dados_tipo : {},
      tipo4: conta.tipo === 4 && conta.dados_tipo ? conta.dados_tipo : {},
      tipo5: conta.tipo === 5 && conta.dados_tipo ? conta.dados_tipo : {},
      tipo6: conta.tipo === 6 && conta.dados_tipo ? conta.dados_tipo : {},
      tipo7: conta.tipo === 7 && conta.dados_tipo ? conta.dados_tipo : {},
      tipo8: conta.tipo === 8 && conta.dados_tipo ? conta.dados_tipo : {},
    };
  };

  var handleGravar = function(values, actions) {
    // Fonte: UManutContas.pas, BitBtn11Click ~linha 5604 — detecção de adulteração tipo 2
    if (!isNovo && values.tipo === 2 && dadosOriginais) {
      var mudouEndereco =
        values.tipo2 && values.tipo2.endereco !== dadosOriginais.endereco;
      var mudouNome =
        values.tipo2 &&
        values.tipo2.razao_social !== dadosOriginais.razao_social;
      var mudouCpf = values.tipo2 && values.tipo2.cpf !== dadosOriginais.cpf;
      if (mudouEndereco && mudouNome && mudouCpf) {
        toast.error(
          'Adulteração detectada: Endereço, Nome e CPF alterados simultaneamente'
        );
        actions.setSubmitting(false);
        return;
      }
    }

    // Validações pré-submit por tipo — Fonte: UManutContas.pas, BitBtn11Click
    var tipoKey = 'tipo' + values.tipo;
    var dadosTipo = values[tipoKey] || {};

    // Fone numérico (TestaNumero) — tipos 1, 2, 4
    if ([1, 2, 4].indexOf(values.tipo) !== -1) {
      if (
        dadosTipo.fone1 &&
        !/^\d*$/.test(String(dadosTipo.fone1).replace(/[\s\-\(\)\.]/g, ''))
      ) {
        toast.error('Fone 1 deve conter apenas números');
        actions.setSubmitting(false);
        return;
      }
      if (
        dadosTipo.fone2 &&
        !/^\d*$/.test(String(dadosTipo.fone2).replace(/[\s\-\(\)\.]/g, ''))
      ) {
        toast.error('Fone 2 deve conter apenas números');
        actions.setSubmitting(false);
        return;
      }
    }

    // Fonte: UManutContas.pas, linhas 5909-5911 e 8836-8850 — CEP numérico, 8 dígitos
    if ([1, 2, 4, 8].indexOf(values.tipo) !== -1 && dadosTipo.cep) {
      var cepLimpo = String(dadosTipo.cep).replace(/[\-\.]/g, '');
      if (!/^\d+$/.test(cepLimpo)) {
        toast.error('CEP deve conter apenas números');
        actions.setSubmitting(false);
        return;
      }
      if (cepLimpo.length !== 8) {
        toast.error(
          'O CEP deve ser informado com 8 dígitos ou deixado em branco'
        );
        actions.setSubmitting(false);
        return;
      }
    }

    // Fonte: UManutContas.pas — CEP dentro da faixa da cidade
    //   linhas 5915-5920 (Tipo 1), 6071-6076 (Tipo 2), 6278-6283 (Tipo 4), 6443-6448 (Tipo 8)
    if (
      [1, 2, 4, 8].indexOf(values.tipo) !== -1 &&
      dadosTipo.cep &&
      dadosTipo.codigo_cidade
    ) {
      var cidadeSel = (lookups.cidades || []).find(function(c) {
        return c.value === dadosTipo.codigo_cidade;
      });
      if (
        cidadeSel &&
        cidadeSel.cepInicial != null &&
        cidadeSel.cepFinal != null
      ) {
        var cepNum = Number(String(dadosTipo.cep).replace(/[\-\.]/g, ''));
        var cepIni = Number(cidadeSel.cepInicial);
        var cepFim = Number(cidadeSel.cepFinal);
        if (!(cepNum >= cepIni && cepNum <= cepFim)) {
          toast.error('CEP informado está fora da faixa da cidade escolhida!');
          actions.setSubmitting(false);
          return;
        }
      }
    }

    // PIX: tipo e chave devem ser preenchidos juntos — tipo 5
    if (values.tipo === 5) {
      if (dadosTipo.pix_tipo && !dadosTipo.pix_chave) {
        toast.error('Informe a Chave PIX');
        actions.setSubmitting(false);
        return;
      }
      if (dadosTipo.pix_chave && !dadosTipo.pix_tipo) {
        toast.error('Informe o Tipo da Chave PIX');
        actions.setSubmitting(false);
        return;
      }
    }

    // Fonte: UManutContas.pas, linhas 6387-6391 — Tipo 6: auto-referência de projeto
    if (values.tipo === 6) {
      var tipoInt = dadosTipo.tipo_interna;
      if (
        (tipoInt === 2 || tipoInt === '2') &&
        dadosTipo.conta_cred_projeto &&
        Number(dadosTipo.conta_cred_projeto) === Number(values.codigo_conta)
      ) {
        toast.error('Conta de Deb Projeto não pode ser a própria Conta');
        actions.setSubmitting(false);
        return;
      }
    }

    // Fonte: UManutContas.pas, BitBtn11Click ~linha 5624 — validação de aprovador
    // DeCriptoSQL(Usuario1Senha.AsString) <> Edit1.Text → "Senha do aprovador não bate"
    if (!values.aprovacao_1) {
      toast.error('Selecione o aprovador');
      actions.setSubmitting(false);
      return;
    }
    if (!senhaAprovador) {
      toast.error('Informe a senha do aprovador');
      actions.setSubmitting(false);
      return;
    }

    validarUsuarioSenha(values.aprovacao_1, senhaAprovador)
      .then(function(valido) {
        if (!valido) {
          toast.error('Senha do aprovador não bate');
          actions.setSubmitting(false);
          return;
        }
        prosseguirAposSenha(values, actions, tipoKey, dadosTipo);
      })
      .catch(function() {
        toast.error('Erro ao validar senha do aprovador');
        actions.setSubmitting(false);
      });
  };

  var prosseguirAposSenha = function(values, actions, tipoKey, dadosTipo) {
    // Fonte: UManutContas.pas, BitBtn11Click ~linha 5970 — verificação de CPF/CNPJ duplicado
    // Extrai CPF do sub-formulário conforme o tipo (1, 2, 4 usam cpf; 8 usa cgc)
    var cpfParaVerificar = null;
    if ([1, 2, 4].indexOf(values.tipo) !== -1 && dadosTipo.cpf) {
      cpfParaVerificar = dadosTipo.cpf;
    } else if (values.tipo === 8 && dadosTipo.cgc) {
      cpfParaVerificar = dadosTipo.cgc;
    }

    var executarSalvamento = function() {
      // Mapear keys do Formik (tipoN) para o backend (dados_tipoN)
      var payload = Object.assign({}, values);
      for (var i = 1; i <= 8; i++) {
        var chaveForm = 'tipo' + i;
        if (payload[chaveForm]) {
          payload['dados_tipo' + i] = Object.assign({}, payload[chaveForm]);
        }
        delete payload[chaveForm];
      }
      // Limpar campos formatados antes do envio. Documento fica string para
      // preservar CNPJ alfanumérico; os demais campos seguem numéricos.
      // Máximo de dígitos por campo (baseado na precisão numeric do SQL Server)
      var maxDigitos = { cep: 8, cpf: 14, cgc: 14, cnpj: 14, rg: 14 };
      var errosConversao = [];
      var limparNumerico = function(val, campo) {
        if (!val && val !== 0) return null;
        var limpo = String(val).replace(/[.\-\/\s\(\)]/g, '');
        if (limpo === '') return null;
        var max = maxDigitos[campo];
        if (max && limpo.length > max) limpo = limpo.substring(0, max);
        var num = Number(limpo);
        if (isNaN(num)) {
          errosConversao.push(campo);
          return null;
        }
        return num;
      };
      var limparDocumento = function(val, campo) {
        if (!val && val !== 0) return null;
        var limpo = cleanCpfCnpj(val);
        if (limpo === '') return null;
        var max = maxDigitos[campo];
        if (max && limpo.length > max) limpo = limpo.substring(0, max);
        return limpo;
      };
      var camposNumericos = [
        'rg',
        'fone1',
        'fone2',
        'fax',
        'celular_contato',
        'celular2',
        'celular3',
        'cep',
        'fone',
        'numero_conta',
        // Campos int (Tipo 5 — Banco): Compromisso, SeqCaixa e pares Pg_*
        'compromisso',
        'seq_caixa',
        'pg_compromisso',
        'pg_seq_caixa',
      ];
      var camposDocumento = ['cpf', 'cnpj', 'cgc'];
      var dadosTipoKey = 'dados_tipo' + values.tipo;
      if (payload[dadosTipoKey]) {
        camposNumericos.forEach(function(campo) {
          if (
            payload[dadosTipoKey][campo] !== undefined &&
            payload[dadosTipoKey][campo] !== null &&
            payload[dadosTipoKey][campo] !== ''
          ) {
            payload[dadosTipoKey][campo] = limparNumerico(
              payload[dadosTipoKey][campo],
              campo
            );
          }
        });
        camposDocumento.forEach(function(campo) {
          if (
            payload[dadosTipoKey][campo] !== undefined &&
            payload[dadosTipoKey][campo] !== null &&
            payload[dadosTipoKey][campo] !== ''
          ) {
            payload[dadosTipoKey][campo] = limparDocumento(
              payload[dadosTipoKey][campo],
              campo
            );
          }
        });
      }
      if (errosConversao.length > 0) {
        toast.error(
          'Campos com caracteres inválidos: ' + errosConversao.join(', ')
        );
        Formik.setSubmitting(false);
        return;
      }
      // Fonte: UManutContas.pas L6170-6175 — campos exclusivos de PF devem ir null quando PJ
      if (payload[dadosTipoKey] && payload[dadosTipoKey].tipo_pessoa === '1') {
        var camposSomentePf = [
          'org_emissor',
          'sexo',
          'estado_civil',
          'data_nascimento',
        ];
        camposSomentePf.forEach(function(campo) {
          payload[dadosTipoKey][campo] = null;
        });
      }
      var fn = isNovo ? inserirConta(payload) : atualizarConta(payload);
      fn.then(function(resp) {
        var resultado = resp;
        // Backend retorna 200 com { sucesso:false, mensagem } em validações de negócio
        if (resultado && resultado.sucesso === false) {
          toast.error(resultado.mensagem || 'Falha ao gravar conta');
          actions.setSubmitting(false);
          return;
        }
        toast.success(
          isNovo ? 'Conta criada com sucesso!' : 'Conta atualizada com sucesso!'
        );

        var novoGe =
          resultado.codigo_grupo_empresa !== null &&
          resultado.codigo_grupo_empresa !== undefined
            ? resultado.codigo_grupo_empresa
            : values.codigo_grupo_empresa;
        var novoCodigo =
          resultado.codigo_conta !== null &&
          resultado.codigo_conta !== undefined
            ? resultado.codigo_conta
            : values.codigo_conta;

        setContaSalva({ ge: novoGe, codigo: novoCodigo });

        // Verificar flags de propagação
        if (resultado.flags) {
          if (resultado.flags.tem_fornecedores_cross_ge) {
            setShowModalFornecedor(true);
          }
          if (resultado.flags.perguntar_banco_empreendimento) {
            setShowModalBanco(true);
          }
        }

        setModoEdicao(false);
        setSenhaAprovador('');
        if (isNovo) {
          history.push('/contas/' + novoGe + '/' + novoCodigo);
        } else {
          // Recarregar dados
          buscarConta(novoGe, novoCodigo)
            .then(function(r) {
              setConta(r);
            })
            .catch(function() {});
        }
      })
        .catch(function(err) {
          // eslint-disable-next-line no-console
          console.error('[Contas/Salvar] erro:', err);
          var msg = 'Erro ao gravar conta';
          if (err) {
            if (typeof err === 'string') msg = err;
            else if (err.mensagem) msg = err.mensagem;
            else if (err.message) msg = err.message;
            else if (err.data && err.data.mensagem) msg = err.data.mensagem;
            else if (err.data && err.data.message) msg = err.data.message;
            else if (err.response && err.response.data) {
              var d = err.response.data;
              msg = d.mensagem || d.message || d.ExceptionMessage || d.Message || msg;
            }
          }
          toast.error(msg);
        })
        .finally(function() {
          actions.setSubmitting(false);
        });
    };

    // Verificação de CPF/CNPJ duplicado antes de salvar
    var documentoParaVerificar = cpfParaVerificar ? cleanCpfCnpj(cpfParaVerificar) : '';
    if (documentoParaVerificar) {
      var excConta = values.codigo_conta || 0;
      var excGe = values.codigo_grupo_empresa || 0;
      verificarDuplicidadeCpf(documentoParaVerificar, excConta, excGe)
        .then(function(duplicados) {
          if (duplicados && duplicados.length > 0) {
            var lista = duplicados
              .map(function(d) {
                return (
                  'GE/Conta: ' +
                  d.codigo_grupo_empresa +
                  '/' +
                  d.codigo_conta +
                  ' - ' +
                  (d.razao_social || '') +
                  ' (' +
                  (d.tipo_conta || '') +
                  ')'
                );
              })
              .join('\n');
            var confirma = window.confirm(
              'CPF/CNPJ já cadastrado na(s) conta(s) abaixo:\n\n' +
                lista +
                '\n\nDeseja continuar com o cadastro?'
            );
            if (confirma) {
              executarSalvamento();
            } else {
              toast.warn('Gravação cancelada — CPF/CNPJ duplicado');
              actions.setSubmitting(false);
            }
          } else {
            executarSalvamento();
          }
        })
        .catch(function() {
          // Em caso de erro na verificação, prosseguir com o salvamento
          executarSalvamento();
        });
    } else {
      executarSalvamento();
    }
  };

  // Sugere código automaticamente quando GE + Tipo estão preenchidos
  var sugerirCodigo = useCallback(
    function(ge, tipo) {
      if (!isNovo || !ge || !tipo) return;
      setCodigoLoading(true);
      sugerirCodigoConta(ge, tipo)
        .then(function(resp) {
          var cod = resp && resp.codigo_sugerido ? resp.codigo_sugerido : null;
          if (cod && formikRef.current) {
            formikRef.current.setFieldValue('codigo_conta', cod);
            toast.info('Código sugerido: ' + cod);
          }
        })
        .catch(function() {})
        .finally(function() {
          setCodigoLoading(false);
        });
    },
    [isNovo]
  );

  // handleTipoChange fora do render do Formik — usa formikRef
  var handleTipoChange = useCallback(
    function(tipo) {
      if (!tipo) return;
      setTipoSelecionado(tipo);
      listarGrupoContas(tipo)
        .then(function(resp) {
          var data = Array.isArray(resp) ? resp : [];
          setLookups(function(l) {
            return {
              ...l,
              gruposContas: data.map(function(g) {
                return {
                  value: g.codigo,
                  label:
                    g.descricao !== null && g.descricao !== undefined
                      ? g.descricao
                      : g.codigo,
                };
              }),
            };
          });
        })
        .catch(function() {});
      var fk = formikRef.current;
      sugerirCodigo(fk ? fk.values.codigo_grupo_empresa : null, tipo);
      // Fonte: UManutContas.pas, linha ~3750 — ao inserir tipo 4, abrir Cadastro Único
      if (isNovo && tipo === 4) {
        var geAtual = fk ? fk.values.codigo_grupo_empresa : null;
        if (geAtual) {
          setShowModalCadUnico(true);
        } else {
          toast.info(
            'Selecione o Grupo de Empresa para abrir a busca de Cadastro Único'
          );
        }
      }
    },
    [isNovo, sugerirCodigo]
  );

  // Fonte: UManutContas.pas — callback quando conta é criada via Cadastro Único
  var handleContaCriadaCadUnico = useCallback(
    function(geCriado, codigoConta) {
      setShowModalCadUnico(false);
      history.push('/contas/' + geCriado + '/' + codigoConta);
    },
    [history]
  );

  // Quando GE muda e tipo já está selecionado, re-sugere código
  // Carrega centros de custo para o GE (necessário para FormTipo3)
  var carregarCentrosCusto = useCallback(function(ge) {
    if (!ge) return;
    listarCentrosCustoLookup(ge)
      .then(function(resp) {
        var data = Array.isArray(resp) ? resp : [];
        setLookups(function(l) {
          return Object.assign({}, l, {
            centrosCusto: data.map(function(item) {
              return {
                value: item.codigo,
                label:
                  item.descricao !== null && item.descricao !== undefined
                    ? item.descricao
                    : String(item.codigo),
              };
            }),
          });
        });
      })
      .catch(function() {});
  }, []);

  var handleGeChange = useCallback(
    function(ge) {
      if (tipoSelecionado) {
        sugerirCodigo(ge, tipoSelecionado);
      }
      // Carrega centros de custo para o GE selecionado
      carregarCentrosCusto(ge);
      // Se tipo=4 já estava selecionado mas GE não, abre CU agora
      if (isNovo && tipoSelecionado === 4 && ge) {
        setShowModalCadUnico(true);
      }
    },
    [isNovo, tipoSelecionado, sugerirCodigo, carregarCentrosCusto]
  );

  // Memoizar valores iniciais — Formik enableReinitialize só recalcula quando conta muda
  var valoresIniciais = useMemo(
    function() {
      return getValoresIniciais();
    },
    [conta]
  );

  // Schema de validação dinâmico — base + tipo-específico
  var currentValidationSchema = useMemo(
    function() {
      return getValidationSchema(tipoSelecionado);
    },
    [tipoSelecionado]
  );

  var tipoAtual = conta ? conta.tipo : null;
  var tabsAtivas = useMemo(
    function() {
      return tipoAtual ? TABS_POR_TIPO[tipoAtual] || [] : [];
    },
    [tipoAtual]
  );

  var headerTitle = isNovo
    ? 'Nova Conta'
    : 'Detalhes da Conta ' +
      codigoParam +
      '/' +
      geParam +
      (conta ? ' - ' + conta.apelido_conta : '');

  if (loading) {
    return (
      <>
        <Header title={headerTitle}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={function() {
              history.push('/contas');
            }}
          >
            <FaArrowLeft className="mr-1" /> Voltar à Lista
          </Button>
        </Header>
        <Container fluid className="px-3 py-3 text-center">
          <Spinner animation="border" variant="primary" />
        </Container>
      </>
    );
  }

  return (
    <>
      <Header title={headerTitle}>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={function() {
            history.push('/contas');
          }}
        >
          <FaArrowLeft className="mr-1" /> Voltar à Lista
        </Button>
      </Header>
      <div className="px-3 py-3 contas-detalhe">
        {/* ── Formik envolve APENAS o formulário (toolbar + campos gerais + campos do tipo) ── */}
        <Formik
          initialValues={valoresIniciais}
          validationSchema={currentValidationSchema}
          onSubmit={handleGravar}
          enableReinitialize
        >
          {function(formik) {
            formikRef.current = formik;

            // Filtrar vendedores/operadores pelo GE selecionado (FK exige mesmo GE)
            var geAtual = formik.values.codigo_grupo_empresa;
            var vendedoresFiltrados = (lookups.vendedoresRaw || []).filter(
              function(v) {
                return v.ge === geAtual;
              }
            );
            var cobradoresFiltrados = (lookups.cobradoresRaw || []).filter(
              function(c) {
                return c.ge === geAtual;
              }
            );
            var lookupsFiltrados = Object.assign({}, lookups, {
              vendedores: vendedoresFiltrados,
              operadores: vendedoresFiltrados,
              cobradores: cobradoresFiltrados,
            });

            return (
              <Form onSubmit={formik.handleSubmit}>
                {/* Toolbar — só botão Editar quando não está em modo edição */}
                {!modoEdicao && !isNovo && (
                  <div className="contas-toolbar">
                    <Button variant="primary" size="sm" onClick={handleEditar}>
                      Editar
                    </Button>
                  </div>
                )}

                {/* Card Aprovação — visível apenas em modo edição, acima de Dados Gerais */}
                {(modoEdicao || isNovo) && (
                  <Card className="shadow-sm mb-3">
                    <Card.Header className="py-2 contas-card-header">
                      <strong>Aprovação</strong>
                    </Card.Header>
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-end flex-wrap">
                        <div className="d-flex align-items-end flex-wrap">
                          <div className="mr-3" style={{ width: 220 }}>
                            <RBForm.Label className="small mb-1">
                              Usuário *
                            </RBForm.Label>
                            <Select
                              placeholder="Selecione..."
                              options={
                                lookups && lookups.responsaveis
                                  ? lookups.responsaveis
                                  : []
                              }
                              value={
                                (lookups && lookups.responsaveis
                                  ? lookups.responsaveis
                                  : []
                                ).find(function(r) {
                                  return r.value === formik.values.aprovacao_1;
                                }) || null
                              }
                              onChange={function(opt) {
                                formik.setFieldValue(
                                  'aprovacao_1',
                                  opt ? opt.value : null
                                );
                                setSenhaAprovador('');
                              }}
                              isLoading={lookupsLoading}
                              isClearable
                              noOptionsMessage={function() {
                                return 'Nenhum usuário';
                              }}
                            />
                          </div>
                          <div style={{ width: 220 }}>
                            <RBForm.Label className="small mb-1">
                              Senha *
                            </RBForm.Label>
                            <RBForm.Control
                              type="password"
                              size="sm"
                              value={senhaAprovador}
                              onChange={function(e) {
                                setSenhaAprovador(e.target.value);
                              }}
                              disabled={!formik.values.aprovacao_1}
                              placeholder={
                                formik.values.aprovacao_1
                                  ? 'Senha do usuário...'
                                  : ''
                              }
                            />
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          <Button
                            variant="success"
                            size="sm"
                            type="button"
                            disabled={
                              formik.isSubmitting ||
                              !formik.values.aprovacao_1 ||
                              !senhaAprovador
                            }
                            onClick={function() {
                              formik.validateForm().then(function(errs) {
                                var msgs = [];
                                var walk = function(obj, prefix) {
                                  Object.keys(obj || {}).forEach(function(k) {
                                    var v = obj[k];
                                    var path = prefix ? prefix + '.' + k : k;
                                    if (typeof v === 'string') msgs.push(path + ': ' + v);
                                    else if (v && typeof v === 'object') walk(v, path);
                                  });
                                };
                                walk(errs, '');
                                if (msgs.length) {
                                  // marca todos os campos como touched p/ exibir erros inline
                                  var touched = {};
                                  var touchAll = function(obj, dest) {
                                    Object.keys(obj || {}).forEach(function(k) {
                                      var v = obj[k];
                                      if (typeof v === 'string') dest[k] = true;
                                      else if (v && typeof v === 'object') {
                                        dest[k] = {};
                                        touchAll(v, dest[k]);
                                      }
                                    });
                                  };
                                  touchAll(errs, touched);
                                  formik.setTouched(touched, false);
                                  toast.error(
                                    'Campos pendentes: ' + msgs.slice(0, 5).join(' | ') +
                                      (msgs.length > 5 ? ' (+' + (msgs.length - 5) + ')' : '')
                                  );
                                  return;
                                }
                                formik.submitForm();
                              });
                            }}
                          >
                            {formik.isSubmitting ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              'Gravar'
                            )}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="ml-2"
                            onClick={function() {
                              formik.resetForm();
                              setModoEdicao(false);
                              setSenhaAprovador('');
                              if (isNovo) history.push('/contas');
                            }}
                            disabled={formik.isSubmitting}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Dados Gerais */}
                <FormDadosGerais
                  formik={formik}
                  modoEdicao={modoEdicao || isNovo}
                  lookups={lookups}
                  lookupsLoading={lookupsLoading}
                  isNovo={isNovo}
                  onTipoChange={handleTipoChange}
                  onGeChange={handleGeChange}
                  codigoLoading={codigoLoading}
                />

                {/* Dados por Tipo — condicional ao tipo selecionado */}
                {formik.values.tipo === 1 && (
                  <FormTipo1
                    formik={formik}
                    modoEdicao={modoEdicao || isNovo}
                    lookups={lookupsFiltrados}
                    lookupsLoading={lookupsLoading}
                  />
                )}
                {formik.values.tipo === 2 && (
                  <FormTipo2
                    formik={formik}
                    modoEdicao={modoEdicao || isNovo}
                    lookups={lookupsFiltrados}
                    lookupsLoading={lookupsLoading}
                    ge={formik.values.codigo_grupo_empresa}
                    codigo={formik.values.codigo_conta}
                    isNovo={isNovo}
                  />
                )}
                {formik.values.tipo === 3 && (
                  <FormTipo3
                    formik={formik}
                    modoEdicao={modoEdicao || isNovo}
                    lookups={lookupsFiltrados}
                    lookupsLoading={lookupsLoading}
                  />
                )}
                {formik.values.tipo === 4 && (
                  <FormTipo4
                    formik={formik}
                    modoEdicao={modoEdicao || isNovo}
                    lookups={lookupsFiltrados}
                    lookupsLoading={lookupsLoading}
                  />
                )}
                {formik.values.tipo === 5 && (
                  <FormTipo5
                    formik={formik}
                    modoEdicao={modoEdicao || isNovo}
                    lookups={lookupsFiltrados}
                    lookupsLoading={lookupsLoading}
                  />
                )}
                {formik.values.tipo === 6 && (
                  <FormTipo6
                    formik={formik}
                    modoEdicao={modoEdicao || isNovo}
                    lookups={lookupsFiltrados}
                    lookupsLoading={lookupsLoading}
                  />
                )}
                {formik.values.tipo === 7 && (
                  <FormTipo7
                    formik={formik}
                    modoEdicao={modoEdicao || isNovo}
                    lookups={lookupsFiltrados}
                    lookupsLoading={lookupsLoading}
                  />
                )}
                {formik.values.tipo === 8 && (
                  <FormTipo8
                    formik={formik}
                    modoEdicao={modoEdicao || isNovo}
                    lookups={lookupsFiltrados}
                    lookupsLoading={lookupsLoading}
                  />
                )}
              </Form>
            );
          }}
        </Formik>

        {/* ── Tabs FORA do Formik — componente isolado, não re-renderiza o formulário ── */}
        {!isNovo && (
          <ContaTabs
            ge={geParam}
            codigo={codigoParam}
            tabsAtivas={tabsAtivas}
            conta={conta}
          />
        )}

        {/* Modais */}
        <ModalPropagarFornecedor
          show={showModalFornecedor}
          ge={contaSalva.ge}
          codigo={contaSalva.codigo}
          onHide={function() {
            setShowModalFornecedor(false);
          }}
          onConcluido={function() {}}
        />
        <ModalBancoEmpreendimento
          show={showModalBanco}
          ge={contaSalva.ge}
          codigo={contaSalva.codigo}
          onHide={function() {
            setShowModalBanco(false);
          }}
          onConcluido={function() {}}
        />
        <ModalCadastroUnico
          show={showModalCadUnico}
          ge={
            formikRef.current
              ? formikRef.current.values.codigo_grupo_empresa
              : null
          }
          onHide={function() {
            setShowModalCadUnico(false);
          }}
          onContaCriada={handleContaCriadaCadUnico}
        />
      </div>
    </>
  );
};

export default ContaDetalhe;
